'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitImagePreview } from '@/fo/ui/kit/image/preview';
import { UiKitPanelBase } from '@/fo/ui/kit/panel/base';
import { UiProductInfoCharacteristics } from '@/fo/ui/product/info/characteristics';
import { UiProductInfoUsageInstructions } from '@/fo/ui/product/info/usage-instructions';
import { UiProductInfoVideo } from '@/fo/ui/product/info/video';
import { MappedProduct } from '@/fo/util/map-product-data';

import { ProductDescription } from '../components/ProductDescription/ProductDescription';

import { styles } from './UiProductInfoPanel.styles';

export interface UiProductInfoPanelProps {
  product: MappedProduct;
  activeIndex?: number;
  onTabChange?: (index: number) => void;
}

export function UiProductInfoPanel({
  product,
  activeIndex,
  onTabChange
}: UiProductInfoPanelProps) {
  const t = useTranslations();
  const slots = styles();

  const tabs = [
    {
      header: t('product.tabs.description'),
      content: <ProductDescription html={product.description_html} />,
      isEmpty: !product.description_html?.trim()
    },
    {
      header: t('product.tabs.characteristics'),
      content: (
        <UiProductInfoCharacteristics
          characteristics={product.characteristics}
        />
      ),
      isEmpty:
        !product.characteristics ||
        Object.keys(product.characteristics).length === 0
    },
    {
      header: t('product.tabs.activeComponents'),
      content: (
        <div className={slots.activeComponents()}>
          <ul className={slots.activeComponentsList()}>
            {product.activeComponents?.map((component, index) => (
              <li
                key={index}
                dangerouslySetInnerHTML={{ __html: component }}
              />
            ))}
          </ul>
        </div>
      ),
      isEmpty:
        !product.activeComponents || product.activeComponents.length === 0
    },
    {
      header: t('product.tabs.composition'),
      content: (
        <div className={slots.composition()}>
          <div
            className={slots.compositionText()}
            dangerouslySetInnerHTML={{ __html: product.composition }}
          />
          <Link
            href="https://ask-naos.ru"
            rel="noopener noreferrer nofollow"
            target="_blank"
            className={slots.compositionLink()}
          >
            <UiKitButtonBase
              color="ghost"
              className={slots.compositionButton()}
            >
              <span className={slots.compositionButtonText()}>
                {t('moreOnAskNaos')}
                <ArrowIcon />
              </span>
            </UiKitButtonBase>
          </Link>
        </div>
      ),
      isEmpty: !product.composition?.trim()
    },
    {
      header: t('product.tabs.usageInstructions'),
      content: (
        <div className={slots.usageInstructions()}>
          <UiProductInfoUsageInstructions
            instructions={product.usageInstructions}
          />
        </div>
      ),
      isEmpty: !product.usageInstructions?.trim()
    },
    {
      header: t('product.tabs.certificates'),
      content: (
        <div className={slots.certificates()}>
          <UiKitImagePreview
            images={[
              {
                id: '1',
                type: 'image',
                src: product.certificateUrl ?? '',
                alt: ''
              }
            ]}
          />
        </div>
      ),
      isEmpty: !product.certificateUrl?.trim()
    }
  ].filter((tab) => !tab.isEmpty);

  return (
    <div
      className={slots.base()}
      id="description"
    >
      <UiKitPanelBase
        activeIndex={activeIndex}
        onTabChange={onTabChange}
        headerTag="h2"
        items={tabs}
      />

      {product.video && <UiProductInfoVideo video={product.video} />}
    </div>
  );
}
